import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ApexCharts from 'apexcharts';
import { Spin } from 'antd';

const CATEGORY_COLORS = ['#00A8FF', '#00E396', '#FEB019', '#FF4560', '#775DD0', '#FF66C3', '#4bc0c8', '#ff9f40'];

const formatPercentage = (percentage) => {
    if (typeof percentage === 'number' && !isNaN(percentage)) {
        return `${percentage.toFixed(2)}%`;
    }
    return '0.00%';
};

const Category = ({ userData, analyticsData, loading, activeCategory, onCategorySelect }) => {

    const [localSelectedCategory, setLocalSelectedCategory] = useState(null);
    const selectedCategory = activeCategory !== undefined ? activeCategory : localSelectedCategory;

    const chartRef = useRef(null);       // DOM node
    const chartInstance = useRef(null);  // ApexCharts instance
    const lastSelectedIdxRef = useRef(null);
    const isMounted = useRef(true);

    useEffect(() => {
        isMounted.current = true;
        return () => { isMounted.current = false; };
    }, []);

    const setSelected = useCallback((nextValue) => {
        onCategorySelect?.(nextValue);
        if (activeCategory === undefined) {
            setLocalSelectedCategory(nextValue);
        }
    }, [activeCategory, onCategorySelect]);

    const chartSeries = useMemo(() =>
            analyticsData.alerts_by_categories.map(item => item.count),
        [analyticsData.alerts_by_categories]
    );

    const chartLabels = useMemo(() =>
            analyticsData.alerts_by_categories.map(item => item.category),
        [analyticsData.alerts_by_categories]
    );

    const totalAlerts = analyticsData.total_alerts;

    const handleCategoryClick = useCallback((categoryName) => {
        const next = selectedCategory === categoryName ? null : categoryName;
        setSelected(next);
    }, [selectedCategory, setSelected]);

    // Build chart options
    const buildOptions = useCallback((labels, series, total, onClick) => ({
        chart: {
            type: 'donut',
            width: '100%',
            events: {
                dataPointSelection: (_event, _chartContext, config) => {
                    const idx = config?.dataPointIndex;
                    const label = typeof idx === 'number' ? labels?.[idx] : null;
                    if (label && label !== 'No Data') {
                        onClick(label);
                    }
                },
            },
        },
        labels: labels.length > 0 ? labels : ['No Data'],
        colors: CATEGORY_COLORS,
        plotOptions: {
            pie: {
                donut: {
                    size: '65%',
                    labels: {
                        show: true,
                        total: {
                            show: true,
                            showAlways: true,
                            label: 'Total Alerts',
                            fontSize: '14px',
                            fontFamily: 'Helvetica, Arial, sans-serif',
                            fontWeight: 600,
                            color: '#373d3f',
                            formatter: () => total.toString(),
                        }
                    }
                }
            }
        },
        dataLabels: { enabled: false },
        legend: { show: false },
        responsive: [{
            breakpoint: 100,
            options: { chart: { width: 70 }, legend: { show: false } }
        }]
    }), []);

    // Create / recreate chart whenever series or labels change
    useEffect(() => {
        if (!chartRef.current || loading) return;

        // Destroy existing instance first
        if (chartInstance.current) {
            try { chartInstance.current.destroy(); } catch (_) {}
            chartInstance.current = null;
        }

        if (chartSeries.length === 0) return;

        const options = buildOptions(chartLabels, chartSeries, totalAlerts, handleCategoryClick);
        const chart = new ApexCharts(chartRef.current, { ...options, series: chartSeries });
        chart.render();
        chartInstance.current = chart;
        lastSelectedIdxRef.current = null;

        return () => {
            if (chartInstance.current) {
                try { chartInstance.current.destroy(); } catch (_) {}
                chartInstance.current = null;
            }
        };
    }, [chartSeries, chartLabels, totalAlerts, loading, buildOptions, handleCategoryClick]);

    // Sync selected category highlight with chart
    useEffect(() => {
        if (!chartInstance.current || !chartLabels?.length) return;

        try {
            if (!selectedCategory) {
                chartInstance.current.clearSelection?.();
                lastSelectedIdxRef.current = null;
                return;
            }

            const nextIdx = chartLabels.indexOf(selectedCategory);
            if (nextIdx < 0) return;

            if (lastSelectedIdxRef.current !== nextIdx) {
                chartInstance.current.clearSelection?.();
                chartInstance.current.toggleDataPointSelection?.(0, nextIdx);
                lastSelectedIdxRef.current = nextIdx;
            }
        } catch (e) {
            // ignore: chart might not be ready yet
        }
    }, [chartLabels, selectedCategory]);

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px', width: '100%' }}>
                <Spin size="large" tip="Loading categories..." />
            </div>
        );
    }

    return (
        <>
            <div className="row g-5">
                <div className="col-md-6 col-12">
                    <div id="chart" className="w-100 d-flex justify-content-center">
                        <div style={{ width: '100%', maxWidth: 420, minHeight: 330 }}>
                            {chartSeries.length > 0 ? (
                                <div ref={chartRef} />
                            ) : (
                                <div
                                    className="w-100 d-flex align-items-center justify-content-center text-gray-500"
                                    style={{ height: 330 }}
                                >
                                    No category data available.
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className="col-md-6 col-12">
                    <p className="fs-4 fw-bold mb-7">Alerts by categories</p>
                    <div className="d-flex flex-column content-justify-center w-100">
                        {analyticsData.alerts_by_categories.map((item, index) => {
                            const isSelected = item.category === selectedCategory;
                            return (
                                <div
                                    key={item.category}
                                    className={`d-flex fs-6 fw-semibold align-items-center mb-4 p-2 rounded ${isSelected ? 'bg-light-primary border border-primary' : ''}`}
                                    onClick={() => handleCategoryClick(item.category)}
                                    style={{ cursor: 'pointer', transition: 'background-color 0.2s, border 0.2s' }}
                                >
                                    <div
                                        className="bullet w-6px h-6px rounded-2 me-3"
                                        style={{ backgroundColor: CATEGORY_COLORS[index % CATEGORY_COLORS.length] }}
                                    />
                                    <div className={`flex-grow-1 me-4 ${isSelected ? 'text-primary fw-bolder' : 'text-gray-800'}`}>{item.category}</div>
                                    <div className="fw-semibold fs-6 text-gray-700 text-xxl-end me-4">
                                        {formatPercentage(item.percentage)}
                                    </div>
                                    <div className="fw-bolder fs-5 text-gray-800 text-xxl-end min-w-100px ">
                                        {item.count}
                                    </div>
                                </div>
                            );
                        })}
                        {analyticsData.alerts_by_categories.length === 0 && (
                            <div className='text-gray-500'>No categories found.</div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default Category;